"use client"

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { Button } from '../ui/button'
import { Kbd } from '../ui/kbd'
import { backuplocalcleanup, backupsql, backupupdateprod, backupzippingsequence, transferbackup, unzippingsequence } from '@/services/backup.service'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { BackupView } from '@/app/deployment/backup/page'
import { deploymentAvailaility } from '@/services/deployment.service'

interface Props {
    domains: BackupView[]
}

export default function BackupTable(props: Props) {
    const [currentDomains, setCurrentDomains] = useState<BackupView[]>([]);
    const [authFailed, setAuthFailed] = useState(false);

    const steps = useMemo(() => [
        {
            title: "Zipping Sequence",
            expectedReturn: "Zipping Done",
            functionToRun: backupzippingsequence,
            data: []
        },
        {
            title: "Unzipping the zip files",
            expectedReturn: "Unzipping Done",
            functionToRun: unzippingsequence,
            data: []
        },
        {
            title: "Backup SQL",
            expectedReturn: "Backup SQL Done",
            functionToRun: backupsql,
            data: []
        },
        {
            title: "Local Cleanup",
            expectedReturn: "Local Cleanup Done",
            functionToRun: backuplocalcleanup,
            data: []
        },
        {
            title: "Transfer Backup",
            expectedReturn: "Transfer Backup Done",
            functionToRun: transferbackup,
            data: []
        },
        {
            title: "Backup Done",
            expectedReturn: "Backup Done",
            functionToRun: backupupdateprod,
            data: []
        },
    ], []);

    useEffect(() => {
        if(currentDomains.length === 0) {
            setCurrentDomains(props.domains);
        }
    }, [currentDomains, props.domains]);

    const flipDomainBackingup = useCallback((domainID: string, to: boolean) => {
        const nextDomains = currentDomains.map(domain => {
            if (domain.ID !== domainID) {
                // No change
                return domain;
            } else {
                // Return a new circle 50px below
                return {
                ...domain,
                BackingUp: to,
                };
            }
        });
        setCurrentDomains(nextDomains);
    }, [currentDomains]);

    const changeDomainBackupAt = useCallback((date: string, domainID: string) => {
        const nextDomains = currentDomains.map(domain => {
            if (domain.ID !== domainID) {
                // No change
                return domain;
            } else {
                // Return a new circle 50px below
                return {
                ...domain,
                BackingUp: false,
                BackupAt: date
                };
            }
        });
        setCurrentDomains(nextDomains);
    }, [currentDomains]);

    const changeDomainBackupTitle = useCallback((title: string, domainID: string) => {
        const nextDomains = currentDomains.map(domain => {
            if (domain.ID !== domainID) {
                // No change
                return domain;
            } else {
                return {
                ...domain,
                BackingUp: true,
                BackupAt: title
                };
            }
        });
        setCurrentDomains(nextDomains);
    }, [currentDomains]);



    async function initiateBackup(domainID:string, index:number){
        flipDomainBackingup(domainID, true);

        const authCheck = await deploymentAvailaility();
        if(authCheck === false){
            setAuthFailed(true);
            return;
        }

        const result = await startStep(index, 0, "", domainID);
        if(!result) {
            console.log(result);
            changeDomainBackupAt("Backup Failed. Check console.log", domainID);
            return;
        }
    }

    const formatFormData = useCallback(
        (domainID:string, zipName:string) => {
          const formData = new FormData();
          formData.append("domainId", domainID);
          formData.append("zipName", zipName);
          return formData;
        },
        []
    );

    const startStep = useCallback(async (index: number, currentStep: number, zipName: string, domainID: string) => {
        let result = null;
        if(steps[currentStep].functionToRun != undefined){
            const getDomain = {...currentDomains[index]}; 
            changeDomainBackupTitle(steps[currentStep].title, getDomain.ID);
            result = await steps[currentStep].functionToRun(
                formatFormData(getDomain.ID, zipName)
            );
            if (result.result == steps[currentStep].expectedReturn) {
                if(zipName.length <= 0 && (result.data.zipName != undefined && result.data.zipName.length > 0)){
                    zipName = result.data.zipName;
                }

                currentStep++;
                if(currentStep < steps.length){
                    if(zipName.length > 0){
                        await startStep(index, currentStep, zipName ?? result.data.zipName, domainID);
                    } else {
                        await startStep(index, currentStep, "", domainID);
                    }
                }

                if(result.data.backedUpAt != undefined){
                    flipDomainBackingup(domainID, false);
                    changeDomainBackupAt(result.data.backedUpAt, domainID);
                }

                return true;
            }
        }
      }, [
        steps,
        currentDomains,
        formatFormData,
        flipDomainBackingup,
        changeDomainBackupAt,
        changeDomainBackupTitle
      ]);
      
    return <div>
        {authFailed && (
            <h1 className="w-full text-xl font-bold text-black dark:text-white mb-2 py-1 px-2 rounded-md bg-red-500">
                Authorization to deploy failed
            </h1>
        )}
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Navn / database</TableHead>
                    <TableHead>Website / mappe</TableHead>
                    <TableHead>Pakke</TableHead>
                    <TableHead>Backup</TableHead>
                    <TableHead>Last Backup</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {currentDomains?.map((domain, index) => (
                    <TableRow key={domain.ID} >
                        <TableCell>{domain.ID}</TableCell>
                        <TableCell>{domain.Name} <Kbd>{domain.DBRel}</Kbd></TableCell>
                        <TableCell>{domain.Website} <Kbd>{domain.Folder}</Kbd></TableCell>
                        <TableCell>{domain.Package}</TableCell>
                        <TableCell><Button className={domain.BackingUp ? "stripedBackground" : "cursor-pointer"} disabled={domain.BackingUp} onClick={() => {initiateBackup(domain.ID, index)}}>Backup</Button></TableCell>
                        <TableCell>{domain.BackupAt ?? "Backup not done yet"}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </div> 
}